import type {
  AgentDrawerFormValues,
  ChannelType,
  CreateAgentPayload,
  EditAgent,
  EscalationRule,
  TestAgentPayload,
  TestEmailAgentPayload,
  ApiCollect,
  ApiEscalation,
  EditAgentSuccessCriteriaItem,
  ResolutionPromptData,
  NewCustomVarInput,
} from './types';

import type { AgentRow, AgentRowWithTooltip, ApiAgent } from './types';
import { parseShipmentDayjs } from '@/common/utils/utils';

// Retell's post-call analysis schema has only string, number and boolean. Every other
// VariableType is asked for as a string. Mirrors pca_utils.variable_type_to_pca_type.
// TODO: delete this once the collect response carries the VariableType itself.
const PCA_TYPE_BY_VARIABLE_TYPE: Record<string, 'number' | 'boolean'> = {
  NUMBER: 'number',
  BOOL: 'boolean',
};

const variableTypeToPcaType = (type: string): 'string' | 'number' | 'boolean' =>
  PCA_TYPE_BY_VARIABLE_TYPE[type] || 'string';

// Stored in the agent's `dynamic_variables`. AiCallFeature's
// CallSession.keypad_enabled enables the keypad only for the exact string "true".
export const IVR_KEYPAD_FLAG = 'ivr_keypad_enabled';

// The backend replaces `dynamic_variables` wholesale on save, so keep every
// existing key and change only the keypad flag. Unchecking removes the flag
// only when it is exactly "true"; any other stored value is left alone.
export const withKeypadFlag = (
  existing: Record<string, unknown> | null | undefined,
  enabled: boolean | undefined
): Record<string, unknown> => {
  const next = { ...(existing ?? {}) };
  if (enabled === undefined) return next;
  if (enabled) next[IVR_KEYPAD_FLAG] = 'true';
  else if (next[IVR_KEYPAD_FLAG] === 'true') delete next[IVR_KEYPAD_FLAG];
  return next;
};

export const parseResolutionPrompt = (
  apiValue: string | null | undefined
): ResolutionPromptData => {
  if (!apiValue) {
    return { type: 'custom', custom_text: '' };
  }

  // Try to parse as JSON
  if (apiValue.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(apiValue);
      return parsed as ResolutionPromptData;
    } catch {
      return { type: 'custom', custom_text: apiValue };
    }
  }
  return { type: 'custom', custom_text: apiValue };
};

export const serializeResolutionPrompt = (
  data: ResolutionPromptData
): string => {
  return JSON.stringify(data);
};

// resolution_prompt names a collect variable by reference name, but the pickers key it by
// collect_id (a row id for a company's own variable). This maps a stored name to the picker value;
// an entry matching no collected variable is returned as it is.
export const collectVarsToCollectIds = (
  collectVars: readonly string[] | undefined,
  collect: EditAgent['collect']
): string[] => {
  const idByName = new Map<string, string>();
  (collect ?? []).forEach((item) => {
    if (item.variable_name) {
      idByName.set(item.variable_name, String(item.collect_id));
    }
  });
  return (collectVars ?? []).map((name) => idByName.get(name) ?? name);
};

export const buildResolutionPromptFromFormFields = (
  values: AgentDrawerFormValues,
  // A variable created in this edit has only a placeholder id, which means nothing once saved.
  // The backend resolves the display name sent in its place to the new variable's reference name.
  pendingCustomVars: Record<string, NewCustomVarInput> = {}
): ResolutionPromptData => {
  const mode = values.success_mode || 'specific';

  if (mode === 'custom') {
    return {
      type: 'custom',
      custom_text: values.success_custom_text || '',
    };
  }

  // Specific mode (preset)
  const presets: Array<{
    id: 'info_collected' | 'no_intervention' | 'positive_sentiment';
    collect_vars?: string[];
  }> = [];

  if (values.success_info_collected) {
    presets.push({
      id: 'info_collected',
      collect_vars: (values.success_collect_selected || []).map(
        (id) => pendingCustomVars[id]?.display_name ?? id
      ),
    });
  }

  if (values.success_no_human) {
    presets.push({ id: 'no_intervention' });
  }

  if (values.success_positive) {
    presets.push({ id: 'positive_sentiment' });
  }

  return {
    type: 'preset',
    presets,
  };
};

export const isValidPhoneNumberLoose = (val?: string) => {
  if (!val) return true;
  const digits = String(val).replace(/[^\d]/g, '');
  return digits.length >= 10 && digits.length <= 15;
};

export const buildIntroPreview = (params: {
  agentName?: string;
  agentRole?: string;
  companyName?: string;
  selfIdentify?: boolean;
  isInbound?: boolean;
}) => {
  const {
    agentName,
    agentRole,
    companyName,
    selfIdentify = true,
    isInbound = false,
  } = params;

  const name = agentName?.trim() || '<agent name>';
  const role = agentRole?.trim() || '<agent role>';
  const company = companyName?.trim() || '<company name>';

  const base = selfIdentify
    ? `Hi, this is ${name}, an AI ${role} at ${company}.`
    : `Hi, this is ${name}, ${role} at ${company}.`;

  return isInbound ? `${base} How can I help you today?` : base;
};

export const buildEmailSignaturePreview = (params: {
  agentName?: string;
  agentRole?: string;
  companyName?: string;
  selfIdentify?: boolean;
}) => {
  const name = params.agentName?.trim() || '<agent name>';
  const role = params.agentRole?.trim() || '<agent role>';
  const company = params.companyName?.trim() || '<company name>';
  const roleWithAI = params.selfIdentify ? `AI ${role}` : role;

  return `Thanks,\n${name}, ${roleWithAI}\n${company}`;
};

type SuccessCriteriaTypeApi = 'specific' | 'custom' | string;

const isCustomType = (t?: SuccessCriteriaTypeApi) => t === 'custom';

const normalizeSpecificCode = (code?: unknown): string => {
  const c = String(code ?? '').trim();
  if (c === 'info_collected') return 'info_collected';
  if (c === 'no_human_intervention') return 'no_human_intervention';
  if (c === 'positive_sentiment') return 'positive_sentiment';
  if (c === 'custom') return 'custom';
  if (c === 'no_human') return 'no_human_intervention';
  if (c === 'positive') return 'positive_sentiment';
  return c;
};

// Rule-owned triggers are kept out of escalate_when on load, but a test call still
// exercises every trigger the agent has.
const allEscalateWhenIds = (
  values: Partial<AgentDrawerFormValues>
): string[] => [
  ...new Set([
    ...(values.escalate_when || []),
    ...(values.escalation_rules || []).flatMap((r) => r.escalate_when || []),
  ]),
];

export const mapEditAgentToFormValues = (
  editAgent: EditAgent
): Partial<AgentDrawerFormValues> => {
  const firstChannel = Array.isArray(editAgent.channel)
    ? editAgent.channel[0]
    : editAgent.channel;

  const nextType: ChannelType =
    firstChannel?.type === 'email' || editAgent?.type === 'email'
      ? 'email'
      : 'phone';

  // The API returns rule-owned triggers in escalation_trigger too. Keep them out of
  // the plain escalate_when field so saving does not re-send them as plain triggers.
  const ruleOwnedTriggerIds = new Set(
    (editAgent.escalation_rules ?? []).flatMap((r) =>
      (r.trigger_ids ?? []).map(String)
    )
  );

  const collectIds = (editAgent.collect ?? []).map((c) => String(c.collect_id));

  const successCriteriaType: 'custom' | 'specific' = isCustomType(
    editAgent.success_criteria_type as SuccessCriteriaTypeApi
  )
    ? 'custom'
    : 'specific';

  const successCriteria = Array.isArray(editAgent.success_criteria)
    ? editAgent.success_criteria
    : [];

  const infoCollectedItem = successCriteria.find(
    (x) => normalizeSpecificCode(x?.specific_code) === 'info_collected'
  );
  const noHumanItem = successCriteria.find(
    (x) => normalizeSpecificCode(x?.specific_code) === 'no_human_intervention'
  );
  const positiveItem = successCriteria.find(
    (x) => normalizeSpecificCode(x?.specific_code) === 'positive_sentiment'
  );

  const customItem =
    successCriteria.find(
      (x) => normalizeSpecificCode(x?.specific_code) === 'custom'
    ) ||
    successCriteria.find((x) => String(x?.data_type || '') === 'custom') ||
    (successCriteriaType === 'custom' ? successCriteria[0] : undefined);

  const extractOptionIds = (
    item: EditAgentSuccessCriteriaItem | undefined
  ): string[] => {
    const raw = item?.options ?? [];
    if (!Array.isArray(raw)) return [];

    return raw
      .map((o) => {
        if (typeof o === 'string') return o;
        if (o && typeof o === 'object' && 'id' in o) return o.id;
        return null;
      })
      .filter((x): x is string => Boolean(x))
      .map(String);
  };

  const success_mode: 'specific' | 'custom' =
    successCriteriaType === 'custom' ? 'custom' : 'specific';

  const success_info_collected = Boolean(infoCollectedItem);
  const success_collect_selected = success_info_collected
    ? extractOptionIds(infoCollectedItem)
    : [];

  const success_no_human = Boolean(noHumanItem);
  const success_positive = Boolean(positiveItem);

  const success_custom_text =
    success_mode === 'custom' ? String(customItem?.custom_text ?? '') : '';

  return {
    channel_type: nextType,
    self_identify: Boolean(editAgent.self_identify),
    ivr_keypad_enabled:
      editAgent.dynamic_variables?.[IVR_KEYPAD_FLAG] === 'true',
    agent_name: editAgent.name ?? '',
    purpose: editAgent.purpose_name ?? '',
    phone_number:
      editAgent.phone_number ??
      (firstChannel?.type === 'phone' ? (firstChannel.value ?? '') : ''),
    email:
      editAgent.email ??
      (firstChannel?.type === 'email' ? (firstChannel.value ?? '') : ''),
    ai_agent_role: editAgent.role ?? '',
    voice: editAgent?.voice?.id ? String(editAgent.voice.id) : undefined,
    behavior_instructions: editAgent.behavior_prompt ?? '',
    advanced_instruction_mode: Boolean(editAgent.advanced_instruction_mode),
    agent_successful_if: parseResolutionPrompt(editAgent.resolution_prompt),
    collect: collectIds,

    // Parse resolution_prompt to populate success fields
    ...((): Pick<
      AgentDrawerFormValues,
      | 'success_mode'
      | 'success_info_collected'
      | 'success_no_human'
      | 'success_positive'
      | 'success_collect_selected'
      | 'success_custom_text'
    > => {
      const resPrompt = parseResolutionPrompt(editAgent.resolution_prompt);

      if (resPrompt.type === 'custom') {
        return {
          success_mode: 'custom',
          success_info_collected: false,
          success_no_human: false,
          success_positive: false,
          success_collect_selected: [],
          success_custom_text: resPrompt.custom_text,
        };
      }

      // Preset type
      const presets = resPrompt.presets || [];
      const infoPreset = presets.find((p) => p.id === 'info_collected');

      return {
        success_mode: 'specific',
        success_info_collected: !!infoPreset,
        success_no_human: presets.some((p) => p.id === 'no_intervention'),
        success_positive: presets.some((p) => p.id === 'positive_sentiment'),
        success_collect_selected: collectVarsToCollectIds(
          infoPreset?.collect_vars,
          editAgent.collect
        ),
        success_custom_text: '',
      };
    })(),
    escalation_phone_number: editAgent.escalation_phone_number ?? '',
    phone_tree: editAgent.phone_tree ?? { enabled: false, options: [] },
    escalation_phone_number_extension:
      editAgent.escalation_phone_number_extension ?? '',
    escalate_when: (editAgent.escalation_trigger ?? [])
      .map((x) => String(x))
      .filter((id) => !ruleOwnedTriggerIds.has(id)),
    escalation_rules: (editAgent.escalation_rules ?? []).map(
      (r): EscalationRule => ({
        id: r.id,
        name: r.name,
        phone_number: r.phone_number,
        extension: r.extension ?? '',
        escalate_when: r.trigger_ids,
      })
    ),
    cc_email: editAgent.cc_email ?? '',

    success_mode,
    success_info_collected,
    success_no_human,
    success_positive,
    success_collect_selected,
    success_custom_text,
  };
};

export const buildCreatePayload = (
  values: AgentDrawerFormValues,
  ctx: {
    agentId?: string | null;
    channelType: ChannelType;
    selfIdentify: boolean;
    companyName: string;
    isInbound: boolean;
    collectShipmentId: boolean;
    customVarIds?: Set<string>;
    pendingCustomVars?: Record<string, NewCustomVarInput>;
    // The saved agent's current dynamic_variables; undefined until they load.
    dynamicVariables?: Record<string, unknown> | null;
  }
): CreateAgentPayload & { id?: string | null } => {
  const ct = values.channel_type || ctx.channelType;
  const selfIdentify = values.self_identify ?? ctx.selfIdentify;

  const ch =
    ct === 'phone'
      ? ({ type: 'phone', value: values.phone_number || '' } as const)
      : ({ type: 'email', value: values.email || '' } as const);

  const mode = (values.success_mode ?? 'specific') as 'specific' | 'custom';

  const infoCollected = Boolean(values.success_info_collected);
  const noHuman = Boolean(values.success_no_human);
  const positive = Boolean(values.success_positive);
  const collectSelectedAll = (values.success_collect_selected ?? []).map(
    String
  );
  const customText = String(values.success_custom_text ?? '');

  // Pending custom vars (from "Generate instructions" suggestions or quick-add)
  // have no real id yet, so they can't be sent as options - resolve them by
  // name instead, same split as collect.new_custom_vars below.
  const pendingCustomVars = ctx.pendingCustomVars || {};
  const collectSelected = collectSelectedAll.filter(
    (id) => !pendingCustomVars[id]
  );
  const collectSelectedNewNames = collectSelectedAll
    .filter((id) => pendingCustomVars[id])
    .map((id) => pendingCustomVars[id].display_name);

  let success_criteria_type: 'specific' | 'custom' = 'specific';
  let success_criteria: Array<{
    has_options: boolean;
    specific_code: string;
    options: string[];
    new_var_names?: string[];
    custom_text: string;
  }> = [];

  if (mode === 'custom') {
    success_criteria_type = 'custom';
    success_criteria = [
      {
        has_options: false,
        specific_code: '',
        options: [],
        custom_text: customText,
      },
    ];
  } else {
    success_criteria_type = 'specific';

    if (infoCollected) {
      success_criteria.push({
        has_options: true,
        specific_code: 'info_collected',
        options: collectSelected,
        new_var_names: collectSelectedNewNames,
        custom_text: '',
      });
    }

    if (noHuman) {
      success_criteria.push({
        has_options: false,
        specific_code: 'no_human_intervention',
        options: [],
        custom_text: '',
      });
    }

    if (positive) {
      success_criteria.push({
        has_options: false,
        specific_code: 'positive_sentiment',
        options: [],
        custom_text: '',
      });
    }

    if (!success_criteria.length) {
      success_criteria = [
        {
          has_options: false,
          specific_code: '',
          options: [],
          custom_text: '',
        },
      ];
    }
  }

  return {
    id: ctx.agentId ?? null,

    name: values.agent_name || '',
    purpose_name: values.purpose?.trim() || '',

    self_identify: selfIdentify,
    role: values.ai_agent_role || '',
    introduction: buildIntroPreview({
      agentName: values.agent_name,
      agentRole: values.ai_agent_role,
      companyName: ctx.companyName,
      selfIdentify,
      isInbound: ctx.isInbound,
    }),

    behavior_prompt: values.behavior_instructions || '',
    advanced_instruction_mode: Boolean(values.advanced_instruction_mode),
    resolution_prompt: serializeResolutionPrompt(
      buildResolutionPromptFromFormFields(values, pendingCustomVars)
    ),

    voice: values.voice ? String(values.voice) : null,
    phone_number: values.phone_number || '',
    email: values.email || '',
    cc_email: values.cc_email || '',
    escalation_phone_number: values.escalation_phone_number || '',
    phone_tree: values.phone_tree ?? { enabled: false, options: [] },
    escalation_phone_number_extension:
      values.escalation_phone_number_extension || '',

    success_criteria_type,
    success_criteria,

    channel: [ch],

    collect: (() => {
      const collectIds = (values.collect || []).map(String);
      const customVarIds = ctx.customVarIds;
      const pendingCustomVars = ctx.pendingCustomVars || {};
      const customIds = customVarIds
        ? collectIds.filter((id) => customVarIds.has(id))
        : [];
      return {
        pca_vars: customVarIds
          ? collectIds.filter((id) => !customVarIds.has(id))
          : collectIds,
        custom_vars: customIds.filter((id) => !pendingCustomVars[id]),
        new_custom_vars: customIds
          .filter((id) => pendingCustomVars[id])
          .map((id) => pendingCustomVars[id]),
      };
    })(),
    escalation_trigger: (values.escalate_when || []).map(String),
    escalation_rules: (values.escalation_rules || []).map((r) => ({
      id: r.id,
      name: r.name,
      phone_number: r.phone_number,
      extension: r.extension || '',
      order: 0,
      trigger_ids: r.escalate_when.map(String),
    })),
    collect_shipment_id: ctx.collectShipmentId,
    // For a saved agent whose details haven't loaded, send nothing rather than
    // a dict that would wipe its stored variables.
    ...(ctx.agentId && ctx.dynamicVariables === undefined
      ? {}
      : {
          dynamic_variables: withKeypadFlag(
            ctx.dynamicVariables,
            values.ivr_keypad_enabled
          ),
        }),
  };
};

export const getDefaultFormValues = (): Partial<AgentDrawerFormValues> => ({
  phone_tree: { enabled: false, options: [] },
  channel_type: 'phone',
  self_identify: false,
  ivr_keypad_enabled: false,
  purpose: '',
  phone_number: '',
  email: '',
  cc_email: '',
  escalation_phone_number: '',
  escalation_phone_number_extension: '',
  escalation_rules: [],

  behavior_instructions: '',
  advanced_instruction_mode: false,
  collect: [],
  escalate_when: [],

  ai_agent_role: 'Logistics Assistant',
  agent_name: 'Mia',
  agent_successful_if: { type: 'custom', custom_text: '' },

  voice: undefined,

  success_mode: 'specific',
  success_info_collected: false,
  success_no_human: false,
  success_positive: false,
  success_collect_selected: [],
  success_custom_text: '',
});

export const buildTestPayload = (
  values: AgentDrawerFormValues,
  ctx: {
    channelType: ChannelType;
    selfIdentify: boolean;
    companyName: string;
    isInbound: boolean;
    rawEscalationData: ApiEscalation[];
    rawCollectData: ApiCollect[];
    toNumber: string;
  }
): TestAgentPayload => {
  const selfIdentify = values.self_identify ?? ctx.selfIdentify;

  const escalationTriggers = allEscalateWhenIds(values)
    .map((id) => {
      const esc = ctx.rawEscalationData.find(
        (e) => String(e.id) === String(id)
      );
      if (!esc) return null;
      return { name: esc.name, prompt: esc.prompt || '' };
    })
    .filter((e): e is { name: string; prompt: string } => e !== null);

  const pcaVars = (values.collect || [])
    .map((id) => {
      const col = ctx.rawCollectData.find((c) => String(c.id) === String(id));
      if (!col) return null;
      return {
        name: col.name,
        description: col.description || '',
        type: variableTypeToPcaType(col.variable_type),
        variable_type: col.variable_type,
        choices: col.choices || null,
      };
    })
    .filter(
      (
        c
      ): c is {
        name: string;
        description: string;
        type: 'string' | 'number' | 'boolean';
        variable_type: string;
        choices: string[] | null;
      } => c !== null
    );

  return {
    voice_id: values.voice || '',
    dynamic_variables: withKeypadFlag(
      undefined,
      !ctx.isInbound && values.ivr_keypad_enabled === true
    ),
    name: values.agent_name || '',
    role: values.ai_agent_role || '',
    introduction: buildIntroPreview({
      agentName: values.agent_name,
      agentRole: values.ai_agent_role,
      companyName: ctx.companyName,
      selfIdentify,
      isInbound: ctx.isInbound,
    }),
    behavior_prompt: values.behavior_instructions || '',
    resolution_prompt: serializeResolutionPrompt(
      buildResolutionPromptFromFormFields(values)
    ),
    phone_number: values.phone_number || '',
    to_number: ctx.toNumber,
    escalation_phone_number: values.escalation_phone_number || '',
    self_identify: selfIdentify,
    escalation_triggers: escalationTriggers,
    pca_vars: pcaVars,
    purpose: values.purpose,
    direction: ctx.isInbound ? 'Inbound' : 'Outbound',
  };
};

export const buildTestEmailPayload = (
  values: AgentDrawerFormValues,
  ctx: {
    channelType: ChannelType;
    selfIdentify: boolean;
    companyName: string;
    isInbound: boolean;
    rawEscalationData: ApiEscalation[];
    rawCollectData: ApiCollect[];
    toEmail: string;
    emailSubject: string;
    emailBodyHtml: string;
    emailBodyText?: string;
    ccEmail?: string;
  }
): TestEmailAgentPayload => {
  const selfIdentify = values.self_identify ?? ctx.selfIdentify;

  const escalationTriggers = allEscalateWhenIds(values)
    .map((id) => {
      const esc = ctx.rawEscalationData.find(
        (e) => String(e.id) === String(id)
      );
      if (!esc) return null;
      return { name: esc.name, prompt: esc.prompt || '' };
    })
    .filter((e): e is { name: string; prompt: string } => e !== null);

  const pcaVars = (values.collect || [])
    .map((id) => {
      const col = ctx.rawCollectData.find((c) => String(c.id) === String(id));
      if (!col) return null;
      return {
        name: col.name,
        description: col.description || '',
        type: variableTypeToPcaType(col.variable_type),
        variable_type: col.variable_type,
        choices: col.choices || null,
      };
    })
    .filter(
      (
        c
      ): c is {
        name: string;
        description: string;
        type: 'string' | 'number' | 'boolean';
        variable_type: string;
        choices: string[] | null;
      } => c !== null
    );

  return {
    email: ctx.toEmail,
    email_subject: ctx.emailSubject,
    email_body_html: ctx.emailBodyHtml,
    email_body_text: ctx.emailBodyText,
    cc_email: ctx.ccEmail,
    name: values.agent_name || '',
    role: values.ai_agent_role || '',
    introduction: buildIntroPreview({
      agentName: values.agent_name,
      agentRole: values.ai_agent_role,
      companyName: ctx.companyName,
      selfIdentify,
      isInbound: ctx.isInbound,
    }),
    behavior_prompt: values.behavior_instructions || '',
    resolution_prompt: serializeResolutionPrompt(
      buildResolutionPromptFromFormFields(values)
    ),
    self_identify: selfIdentify,
    escalation_triggers: escalationTriggers,
    pca_vars: pcaVars,
    purpose: values.purpose,
    from_email: values.email || undefined,
    direction: ctx.isInbound ? 'Inbound' : 'Outbound',
  };
};

export const relativeFromNow = (iso: string) => {
  const parsed = parseShipmentDayjs(iso);
  const d = parsed ? parsed.toDate() : new Date(iso);
  const now = new Date();
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}mins ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}hrs ago`;
  const diffDays = Math.floor(diffHrs / 24);
  return `${diffDays} days ago`;
};

export const toIsoIfPossible = (value: string | null) => {
  if (!value) return null;
  const parsed = parseShipmentDayjs(value);
  if (parsed) return parsed.toDate().toISOString();
  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

type ChannelTypeLiteral = 'phone' | 'email';

const isChannelType = (t: unknown): t is ChannelTypeLiteral =>
  t === 'phone' || t === 'email';

export const mapApiAgentToRow = (
  a: ApiAgent,
  formatDateTime: (value?: string | null) => string
): AgentRowWithTooltip => {
  const lastTriggeredIso = toIsoIfPossible(
    a.last_triggered?.triggered_at ?? null
  );

  const workflows =
    a.last_triggered?.workflows?.map((wf) => ({
      id: wf.id,
      name: wf.name,
      triggered_at: toIsoIfPossible(wf.triggered_at) || wf.triggered_at,
    })) || [];

  const baseChannel: AgentRow['channel'] = Array.isArray(a.channel)
    ? a.channel
        .filter((c) => isChannelType(c?.type) && !!c?.value)
        .map((c) => ({
          type: c.type as ChannelTypeLiteral,
          value: String(c.value),
        }))
    : [];

  const derivedChannel: AgentRow['channel'] = [
    ...(a.phone_number
      ? [{ type: 'phone' as const, value: String(a.phone_number) }]
      : []),
    ...(a.email ? [{ type: 'email' as const, value: String(a.email) }] : []),
  ];

  const seen = new Set<string>();
  const channel: AgentRow['channel'] = [
    ...baseChannel,
    ...derivedChannel,
  ].filter((c) => {
    const key = `${c.type}:${c.value}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const usageRaw = a.metrics_7d?.usage;
  const usage_7d = usageRaw
    ? {
        call: Number(usageRaw.call ?? 0),
        email: Number(usageRaw.email ?? 0),
        total: Number(
          usageRaw.total ??
            Number(usageRaw.call ?? 0) + Number(usageRaw.email ?? 0)
        ),
      }
    : null;

  const successRaw = a.metrics_7d?.success_rate;
  const success_7d = successRaw
    ? {
        call: String(successRaw.call ?? '0%').trim(),
        email: String(successRaw.email ?? '0%').trim(),
        total: String(successRaw.total ?? '0%').trim(),
      }
    : null;

  return {
    id: a.id,
    name: a.name || '--',
    purpose: a.purpose_name || '--',
    purpose_name: a.purpose_name || '',
    agent_type: a.agent_type || '',
    channel,
    collect: (a.collect || []).map((c) => ({
      key: c.collect_id || String(c.id),
      label: c.display_name,
      type: 'string',
      required: false,
    })),
    last_triggered_at: lastTriggeredIso,
    last_triggered_label: lastTriggeredIso
      ? relativeFromNow(lastTriggeredIso)
      : '--',
    last_triggered: {
      at: lastTriggeredIso,
      relative_label: lastTriggeredIso
        ? relativeFromNow(lastTriggeredIso)
        : '--',
      workflows,
    },
    usage_7d,
    success_7d,
    updated_at: toIsoIfPossible(a.updated_at),
    updated_label: a.updated_at ? formatDateTime(a.updated_at) || '--' : '--',
  };
};
